import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import type { StatusToastTone } from '@app/components/StatusToast';
import Tabs from '@app/components/Tabs';
import CopyField from '@app/components/CopyField';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import { PauseIcon, PencilIcon, PlayIcon, SaveIcon, TrashIcon } from '@app/components/icons';
import { useIdpConnectionsApi, useIdpMappingRulesApi, useAuthApi, useRolesApi, useSecretsApi } from '@app/hooks/useApis';
import {
  deriveConnectionSource,
  formatConnectionSourceBadge,
  formatConnectionStatusBadge,
  isOidcConnection,
  isSamlConnection,
  summarizeMappingRule,
  type MappingRulePreview
} from '@app/utils/idp';
import { formatDateTime } from '@app/utils/format';
import type {
  IdpConnection,
  IdpConnectionUpdate,
  RoleMappingRule,
  RoleMappingRuleCreate,
  RoleMappingRuleCreateMatchSourceEnum,
  RoleMappingRuleCreateMatchOperatorEnum,
  RoleMappingRuleCreateResult,
  RoleMappingRuleUpdate
} from '@app/types/sdk';
import { ResponseError } from '@app/types/sdk';
import { getTokenResponseOrThrow } from '@app/utils/tokens';
import type { TokenGrantRequest } from '@app/types/auth';
import { toTokenRequest } from '@app/types/auth';
import { deleteSecretWithAuth } from '@app/utils/secrets';
import { resolveBasePath } from '@app/utils/api';
import { useConfig } from '@config/provider';

export interface ConnectionDrawerProps {
  orgId: string;
  connectionId: string | null;
  open: boolean;
  onClose: () => void;
  onUpdated: (connection: IdpConnection, etag?: string | null) => void;
  onDeleted: (feedback?: { tone: StatusToastTone; message: string }) => void;
  onDeleteFeedback?: (feedback: { tone: StatusToastTone; message: string }) => void;
  initialTab?: DrawerTab;
}

export type DrawerTab = 'overview' | 'checklist' | 'test';

interface MappingRuleFormState {
  source: RoleMappingRuleCreateMatchSourceEnum;
  path: string;
  operator: RoleMappingRuleCreateMatchOperatorEnum;
  value: string;
  enabled: boolean;
}

interface RoleSlugChip {
  id: string;
  slug: string;
}

interface MappingRow {
  id: string;
  rule: RoleMappingRule;
  preview: MappingRulePreview;
}

interface SummaryEntry {
  label: string;
  value: ReactNode;
}

const createDefaultMappingFormState = (): MappingRuleFormState => ({
  source: 'group' as RoleMappingRuleCreateMatchSourceEnum,
  path: '',
  operator: 'equals' as RoleMappingRuleCreateMatchOperatorEnum,
  value: '',
  enabled: true
});

const ConnectionDrawer = ({ orgId, connectionId, open, onClose, onUpdated, onDeleted, onDeleteFeedback, initialTab = 'overview' }: ConnectionDrawerProps) => {
  const idpConnectionsApi = useIdpConnectionsApi();
  const mappingRulesApi = useIdpMappingRulesApi();
  const authApi = useAuthApi();
  const rolesApi = useRolesApi();
  const secretsApi = useSecretsApi();
  const { apiBaseUrl } = useConfig();
  const basePath = useMemo(() => resolveBasePath(apiBaseUrl), [apiBaseUrl]);
  const [connection, setConnection] = useState<IdpConnection | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsDescription, setDetailsDescription] = useState('');
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSuccess, setDetailsSuccess] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const [mappingRules, setMappingRules] = useState<RoleMappingRule[]>([]);
  const [mappingEtags, setMappingEtags] = useState<Record<string, string>>({});
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [mappingForm, setMappingForm] = useState<MappingRuleFormState>(createDefaultMappingFormState());
  const [mappingSubmitting, setMappingSubmitting] = useState(false);
  const [pendingMappingDeletes, setPendingMappingDeletes] = useState<MappingRow[] | null>(null);
  const [mappingDeleteBusy, setMappingDeleteBusy] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [roleSlugChips, setRoleSlugChips] = useState<RoleSlugChip[]>([]);
  const [roleOptions, setRoleOptions] = useState<Array<{ id?: string; slug: string; name?: string; status?: string }>>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [roleSlugSelection, setRoleSlugSelection] = useState<string>('');
  const [mappingPage, setMappingPage] = useState(1);
  const [mappingPageSize, setMappingPageSize] = useState(5);
  const [rotateSecretValue, setRotateSecretValue] = useState('');
  const [rotateSecretBusy, setRotateSecretBusy] = useState(false);
  const [rotateSecretError, setRotateSecretError] = useState<string | null>(null);
  const [rotateSecretSuccess, setRotateSecretSuccess] = useState<string | null>(null);
  const isEditing = editingRuleId != null;
  const selectedRoleSlugs = useMemo(() => roleSlugChips.map((chip) => chip.slug), [roleSlugChips]);
  const availableRoleOptions = useMemo(
    () => roleOptions.filter((role) => !selectedRoleSlugs.includes(role.slug)),
    [roleOptions, selectedRoleSlugs]
  );
  const formatRoleOptionLabel = useCallback((role: { slug: string }) => role.slug, []);
  const formatSelectedRoleLabel = useCallback(
    (slug: string) => {
      const role = roleOptions.find((option) => option.slug === slug);
      if (!role) {
        return slug;
      }
      return formatRoleOptionLabel(role);
    },
    [formatRoleOptionLabel, roleOptions]
  );

  const [testEmail, setTestEmail] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [testDetails, setTestDetails] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DrawerTab>(initialTab);
  const [mappingDrawerOpen, setMappingDrawerOpen] = useState(false);
  const logoutTestSession = useCallback(
    async (token: string) => {
      const logoutUrl = `${basePath}/auth/logout`;
      try {
        const response = await fetch(logoutUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.warn('[test-login] Failed to revoke test session', error);
      }
    },
    [basePath]
  );


  const resetState = useCallback(() => {
    setConnection(null);
    setEtag(null);
    setError(null);
    setActionError(null);
    setActionBusy(false);
    setMappingRules([]);
    setMappingEtags({});
    setMappingLoading(false);
    setMappingError(null);
    setMappingForm(createDefaultMappingFormState());
    setMappingSubmitting(false);
    setPendingMappingDeletes(null);
    setMappingDeleteBusy(false);
    setDeleteConfirmationOpen(false);
    setRoleSlugChips([]);
    setEditingRuleId(null);
    setRoleSlugSelection('');
    setRoleOptions([]);
    setRolesLoading(false);
    setRolesError(null);
    setRotateSecretValue('');
    setRotateSecretBusy(false);
    setRotateSecretError(null);
    setRotateSecretSuccess(null);
    setTestEmail('');
    setTestBusy(false);
    setTestStatus(null);
    setTestDetails(null);
    setActiveTab(initialTab);
    setMappingDrawerOpen(false);
  }, [initialTab]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (!connectionId) {
      return;
    }
    let isMounted = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    idpConnectionsApi
      .orgsOrgIdIdpConnectionsConnectionIdGetRaw({ orgId, connectionId }, { signal: controller.signal })
      .then(async (response: any) => {
        if (!isMounted || controller.signal.aborted) {
          return;
        }
        const body = await response.value();
        setConnection(body);
        const headerEtag = response.raw.headers.get('ETag');
        setEtag(headerEtag);
        setDetailsDescription(body.description ?? '');
        setDetailsSuccess(null);
        setDetailsError(null);
        setRotateSecretValue('');
        setRotateSecretError(null);
        setRotateSecretSuccess(null);
        setRotateSecretBusy(false);
      })
      .catch((caught: unknown) => {
        if (!isMounted || controller.signal.aborted) {
          return;
        }
        if (caught instanceof ResponseError) {
          setError(`Failed to load connection (HTTP ${caught.response.status}).`);
        } else {
          setError('Failed to load connection.');
        }
      })
      .finally(() => {
        if (!isMounted || controller.signal.aborted) {
          return;
        }
        setLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [connectionId, idpConnectionsApi, open, orgId, resetState]);
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const refreshMappingRules = useCallback(async () => {
    if (!connectionId) {
      return;
    }
    setMappingLoading(true);
    setMappingError(null);
    try {
      const response = await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsGetRaw({ orgId, connectionId, limit: 200 });
      const body = await response.value();
      const items = (body.items ?? []) as RoleMappingRule[];
      setMappingRules(items);
      const etags: Record<string, string> = {};
      await Promise.all(
        items.map(async (rule) => {
          const ruleId = rule.id;
          if (!ruleId) {
            return;
          }
          try {
            const ruleResponse = await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsRuleIdGetRaw(
              { orgId, connectionId, ruleId },
              undefined
            );
            const ruleEtag = ruleResponse.raw.headers.get('ETag');
            if (ruleEtag) {
              etags[ruleId] = ruleEtag;
            }
          } catch {
            // ignore failures for individual rule fetches
          }
        })
      );
      setMappingEtags(etags);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setMappingError(`Failed to load mapping rules (HTTP ${caught.response.status}).`);
      } else {
        setMappingError('Failed to load mapping rules.');
      }
    } finally {
      setMappingLoading(false);
    }
  }, [connectionId, mappingRulesApi, orgId]);

  const refreshRoleOptions = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setRolesLoading(true);
    setRolesError(null);
    try {
      const response = await rolesApi.orgsOrgIdRolesGet({ orgId, limit: 200 });
      const items = response.items ?? [];
      const options = items
        .map((role: any) => ({
          id: role.id ?? undefined,
          slug: role.slug ?? '',
          name: role.name ?? undefined,
          status: role.status ?? undefined
        }))
        .filter((role) => role.slug.length > 0);
      setRoleOptions(options);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setRolesError(`Failed to load role slugs (HTTP ${caught.response.status}).`);
      } else {
        setRolesError('Failed to load role slugs.');
      }
    } finally {
      setRolesLoading(false);
    }
  }, [orgId, rolesApi]);

  useEffect(() => {
    if (open && connectionId) {
      void refreshMappingRules();
    }
  }, [connectionId, open, refreshMappingRules]);

  useEffect(() => {
    if (open) {
      void refreshRoleOptions();
    }
  }, [open, refreshRoleOptions]);

  useEffect(() => {
    if (roleSlugSelection && !availableRoleOptions.some((option) => option.slug === roleSlugSelection)) {
      setRoleSlugSelection('');
    }
  }, [availableRoleOptions, roleSlugSelection]);

  useEffect(() => {
    setMappingPage(1);
  }, [mappingRules.length]);


  const handleStatusToggle = async () => {
    if (!connection || !etag) {
      return;
    }
    if (!connection.id) {
      setActionError('Missing connection identifier; reload and try again.');
      return;
    }
    try {
      setActionBusy(true);
      setActionError(null);
      const nextStatus: 'active' | 'disabled' = connection.status === 'active' ? 'disabled' : 'active';
      const payload: IdpConnectionUpdate =
        connection.type === 'oidc'
          ? { type: 'oidc', status: nextStatus }
          : { type: 'saml', status: nextStatus };
      const response = await idpConnectionsApi.orgsOrgIdIdpConnectionsConnectionIdPatchRaw({
        orgId,
        connectionId: connection.id!,
        ifMatch: etag,
        idpConnectionUpdate: payload
      });
      const updated = await response.value();
      const nextEtag = response.raw.headers.get('ETag');
      setConnection(updated);
      setEtag(nextEtag ?? null);
      onUpdated(updated, nextEtag ?? null);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setActionError(`Failed to update status (HTTP ${caught.response.status}).`);
      } else {
        setActionError('Failed to update status.');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!connection || !etag) {
      return;
    }
    if (!connection.id) {
      setDetailsError('Missing connection identifier; reload and try again.');
      return;
    }
    try {
      setDetailsSaving(true);
      setDetailsError(null);
      setDetailsSuccess(null);
      const payload: IdpConnectionUpdate =
        connection.type === 'oidc'
          ? { type: 'oidc', description: detailsDescription.trim() || undefined }
          : { type: 'saml', description: detailsDescription.trim() || undefined };
      const response = await idpConnectionsApi.orgsOrgIdIdpConnectionsConnectionIdPatchRaw({
        orgId,
        connectionId: connection.id,
        ifMatch: etag,
        idpConnectionUpdate: payload
      });
      const updated = await response.value();
      const nextEtag = response.raw.headers.get('ETag');
      setConnection(updated);
      setEtag(nextEtag ?? null);
      setDetailsSuccess('Connection updated.');
      onUpdated(updated, nextEtag ?? null);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setDetailsError(`Failed to save changes (HTTP ${caught.response.status}).`);
      } else if (caught instanceof Error) {
        setDetailsError(caught.message);
      } else {
        setDetailsError('Failed to save changes.');
      }
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!connection || !etag || actionBusy) {
      return;
    }
    if (!connection.id) {
      setActionError('Missing connection identifier; reload and try again.');
      return;
    }
    try {
      setActionBusy(true);
      setActionError(null);
      await idpConnectionsApi.orgsOrgIdIdpConnectionsConnectionIdDelete({
        orgId,
        connectionId: connection.id!,
        ifMatch: etag
      });
      let secretCleanupError: string | null = null;
      if (isOidcConnection(connection)) {
        const clientSecretId = connection.config.clientSecretId;
        if (clientSecretId) {
          try {
            await deleteSecretWithAuth({ secretsApi, orgId, secretId: clientSecretId });
          } catch (caught: unknown) {
            if (caught instanceof ResponseError) {
              secretCleanupError = `Connection deleted but failed to delete client secret (HTTP ${caught.response.status}).`;
            } else if (caught instanceof Error) {
              secretCleanupError = `Connection deleted but failed to delete client secret. ${caught.message}`;
            } else {
              secretCleanupError = 'Connection deleted but failed to delete client secret.';
            }
          }
        }
      }
      if (secretCleanupError) {
        setConnection(null);
        setEtag(null);
        onDeleted({ tone: 'warning', message: 'Connection deleted, but its stored secret could not be removed.' });
      } else {
        onDeleted({ tone: 'success', message: `${connection.name ?? 'Connection'} deleted successfully.` });
      }
    } catch (caught: unknown) {
      let message = 'Failed to delete connection.';
      if (caught instanceof ResponseError) {
        message = `Failed to delete connection (HTTP ${caught.response.status}).`;
      }
      setActionError(message);
      onDeleteFeedback?.({ tone: 'error', message });
    } finally {
      setActionBusy(false);
    }
  };

  const handleRotateSecret = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!connection || !isOidcConnection(connection)) {
      return;
    }
    const secretId = connection.config.clientSecretId;
    if (!secretId) {
      setRotateSecretError('Client secret metadata is missing. Reload and try again.');
      return;
    }
    if (rotateSecretValue.trim().length === 0) {
      setRotateSecretError('Enter a new client secret value.');
      return;
    }
    try {
      setRotateSecretBusy(true);
      setRotateSecretError(null);
      setRotateSecretSuccess(null);
      const metadataResponse = await secretsApi.orgsOrgIdSecretsSecretIdGetRaw({ orgId, secretId });
      const secretEtag = metadataResponse.raw.headers.get('ETag') ?? undefined;
      await secretsApi.orgsOrgIdSecretsSecretIdValuePost({
        orgId,
        secretId,
        ifMatch: secretEtag ?? undefined,
        secretSetValue: { value: rotateSecretValue }
      });
      setRotateSecretValue('');
      setRotateSecretSuccess('Client secret updated.');
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        const text = await caught.response.text();
        setRotateSecretError(`Failed to update client secret (HTTP ${caught.response.status}). ${text || ''}`.trim());
      } else if (caught instanceof Error) {
        setRotateSecretError(caught.message);
      } else {
        setRotateSecretError('Failed to update client secret.');
      }
    } finally {
      setRotateSecretBusy(false);
    }
  };

  const handleMappingFormChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = event.target;
    const { name, value } = target;
    const key = name as keyof MappingRuleFormState;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    setMappingForm((prev) => {
      const next: MappingRuleFormState = {
        ...prev,
        [key]: (isCheckbox ? target.checked : value) as MappingRuleFormState[typeof key]
      };
      if (key === 'source' && value === 'group') {
        next.path = '';
      }
      return next;
    });
  };

const handleRoleSlugSelect = (event: ChangeEvent<HTMLSelectElement>) => {
  const slug = event.target.value;
  if (!slug) {
    setRoleSlugSelection('');
    return;
  }
  setRoleSlugChips((prev) => {
    if (prev.some((chip) => chip.slug === slug)) {
      return prev;
    }
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    return [...prev, { id, slug }];
  });
  setRoleSlugSelection('');
};

const handleRoleSlugRemove = (chipId: string) => {
  setRoleSlugChips((prev) => prev.filter((chip) => chip.id !== chipId));
};
  const handleEditRule = (rule: RoleMappingRule) => {
    if (!rule.id) {
      setMappingError('Missing rule identifier; reload and try again.');
      return;
    }
    const match = (rule.match ?? {}) as Record<string, any>;
    const result = (rule.result ?? {}) as Record<string, any>;
    const roleSlugs = Array.isArray(result.roleSlugs)
      ? (result.roleSlugs as unknown[])
          .filter((value): value is string => typeof value === 'string')
      : [];
    setMappingForm({
      source: (match.source as RoleMappingRuleCreateMatchSourceEnum) ?? ('group' as RoleMappingRuleCreateMatchSourceEnum),
      path: typeof match.path === 'string' ? match.path : '',
      operator: (match.operator as RoleMappingRuleCreateMatchOperatorEnum) ?? ('equals' as RoleMappingRuleCreateMatchOperatorEnum),
      value: typeof match.value === 'string' ? match.value : '',
      enabled: rule.enabled ?? true
    });
    setRoleSlugChips(
      roleSlugs.map((slug, index) => ({
        slug,
        id:
          (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${slug}-${index}-${Math.random().toString(36).slice(2, 6)}`)
      }))
    );
    setEditingRuleId(rule.id);
    setMappingError(null);
    setRoleSlugSelection('');
    setMappingDrawerOpen(true);
  };

  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setMappingForm(createDefaultMappingFormState());
    setRoleSlugChips([]);
    setRoleSlugSelection('');
    setMappingError(null);
  };

  const handleMappingDrawerClose = () => {
    handleCancelEdit();
    setMappingDrawerOpen(false);
  };

  const handleMappingAdd = () => {
    handleCancelEdit();
    setMappingDrawerOpen(true);
  };

  const handleMappingSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!connectionId) {
      return;
    }
    if (mappingForm.value.trim().length === 0 || selectedRoleSlugs.length === 0) {
      setMappingError('Provide both a match value and at least one resulting role slug.');
      return;
    }
    if (isEditing && editingRuleId) {
      const storedEtag = mappingEtags[editingRuleId];
      if (!storedEtag) {
        setMappingError('Missing rule ETag; reload and try again.');
        return;
      }
    }
    const matchPayload = {
      source: mappingForm.source,
      operator: mappingForm.operator,
      value: mappingForm.value.trim(),
      path: mappingForm.source === 'group' ? undefined : mappingForm.path.trim() || undefined
    };
    setMappingSubmitting(true);
    setMappingError(null);
    try {
      if (isEditing && editingRuleId) {
        const storedEtag = mappingEtags[editingRuleId]!;
        const update: RoleMappingRuleUpdate = {
          enabled: mappingForm.enabled,
          match: matchPayload,
          result: {
            roleSlugs: [...selectedRoleSlugs]
          }
        };
        const response = await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsRuleIdPatchRaw({
          orgId,
          connectionId,
          ruleId: editingRuleId,
          ifMatch: storedEtag,
          roleMappingRuleUpdate: update
        });
        const updated = await response.value();
        const nextEtag = response.raw.headers.get('ETag');
        const updatedRuleId = updated.id ?? editingRuleId;
        setMappingRules((prev) => prev.map((item) => (item.id === updatedRuleId ? updated : item)));
        if (nextEtag) {
          setMappingEtags((prev) => ({ ...prev, [updatedRuleId]: nextEtag }));
        }
      } else {
        const request: RoleMappingRuleCreate = {
          enabled: mappingForm.enabled,
          match: matchPayload,
          result: {
            roleSlugs: [...selectedRoleSlugs]
          } as RoleMappingRuleCreateResult
        };
        await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsPost({
          orgId,
          connectionId,
          roleMappingRuleCreate: request
        });
        await refreshMappingRules();
      }
      setMappingForm(createDefaultMappingFormState());
      setRoleSlugChips([]);
      setRoleSlugSelection('');
      setEditingRuleId(null);
      setMappingDrawerOpen(false);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setMappingError(
          isEditing
            ? `Failed to update mapping rule (HTTP ${caught.response.status}).`
            : `Failed to create mapping rule (HTTP ${caught.response.status}).`
        );
      } else {
        setMappingError(isEditing ? 'Failed to update mapping rule.' : 'Failed to create mapping rule.');
      }
    } finally {
      setMappingSubmitting(false);
    }
  };

  const handleMappingToggle = async (rule: RoleMappingRule) => {
    if (!connectionId) {
      return;
    }
    const ruleId = rule.id;
    if (!ruleId) {
      setMappingError('Missing rule identifier; reload and try again.');
      return;
    }
    const storedEtag = mappingEtags[ruleId];
    if (!storedEtag) {
      setMappingError('Missing rule ETag; reload and try again.');
      return;
    }
    const update: RoleMappingRuleUpdate = {
      enabled: !rule.enabled
    };
    try {
      const response = await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsRuleIdPatchRaw({
        orgId,
        connectionId,
        ruleId,
        ifMatch: storedEtag,
        roleMappingRuleUpdate: update
      });
      const updated = await response.value();
      const nextEtag = response.raw.headers.get('ETag');
      const updatedRuleId = updated.id ?? ruleId;
      setMappingRules((prev) => prev.map((item) => (item.id === updatedRuleId ? updated : item)));
      if (nextEtag) {
        setMappingEtags((prev) => ({ ...prev, [updatedRuleId]: nextEtag }));
      }
      if (editingRuleId === updatedRuleId) {
        setMappingForm((prev) => ({ ...prev, enabled: updated.enabled ?? prev.enabled }));
      }
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setMappingError(`Failed to update rule (HTTP ${caught.response.status}).`);
      } else {
        setMappingError('Failed to update rule.');
      }
    }
  };

  const handleMappingDelete = async (rule: RoleMappingRule): Promise<boolean> => {
    if (!connectionId) {
      return false;
    }
    const ruleId = rule.id;
    if (!ruleId) {
      setMappingError('Missing rule identifier; reload and try again.');
      return false;
    }
    const storedEtag = mappingEtags[ruleId];
    if (!storedEtag) {
      setMappingError('Missing rule ETag; reload and try again.');
      return false;
    }
    try {
      await mappingRulesApi.orgsOrgIdIdpConnectionsConnectionIdMappingsRuleIdDelete({
        orgId,
        connectionId,
        ruleId,
        ifMatch: storedEtag
      });
      setMappingRules((prev) => prev.filter((item) => item.id !== ruleId));
      setMappingEtags((prev) => {
        const next = { ...prev };
        delete next[ruleId];
        return next;
      });
      if (editingRuleId === ruleId) {
        handleCancelEdit();
      }
      return true;
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setMappingError(`Failed to delete rule (HTTP ${caught.response.status}).`);
      } else {
        setMappingError('Failed to delete rule.');
      }
      return false;
    }
  };

  const handleBulkDeleteRules = (rows: MappingRow[]) => {
    if (rows.length === 0 || mappingDeleteBusy) {
      return;
    }
    const rules = rows.map((row) => row.rule).filter((rule) => rule.id);
    if (rules.length === 0) {
      return;
    }
    setPendingMappingDeletes(rows);
  };

  const confirmMappingRuleDeletes = async () => {
    if (!pendingMappingDeletes || pendingMappingDeletes.length === 0 || mappingDeleteBusy) {
      return;
    }
    setMappingDeleteBusy(true);
    setMappingError(null);
    const results = await Promise.all(pendingMappingDeletes.map((row) => handleMappingDelete(row.rule)));
    const deletedCount = results.filter(Boolean).length;
    const failedCount = results.length - deletedCount;
    if (deletedCount > 0) {
      setPendingMappingDeletes(null);
      onDeleteFeedback?.({
        tone: failedCount > 0 ? 'warning' : 'success',
        message:
          failedCount > 0
            ? `${deletedCount} of ${results.length} mapping rules deleted. ${failedCount} could not be deleted.`
            : `${deletedCount} mapping rule${deletedCount === 1 ? '' : 's'} deleted successfully.`
      });
    } else {
      onDeleteFeedback?.({ tone: 'error', message: 'Failed to delete the selected mapping rules.' });
    }
    setMappingDeleteBusy(false);
  };

  const handleTestLogin = async () => {
    if (!connectionId || !connection) {
      return;
    }
    try {
      setTestBusy(true);
      setTestDetails(null);
      const baseStartUrl = (connection as IdpConnection & { startUrl?: string | null }).startUrl ?? null;
      if (!baseStartUrl) {
        setTestStatus('This connection does not expose a start URL. Enable SP-initiated sign-in and try again.');
        setTestDetails(null);
        return;
      }

      setTestStatus('Opening test login…');
      const emailHint = testEmail.trim();
      let launchUrl = baseStartUrl;
      try {
        const url = new URL(baseStartUrl, window.location.origin);
        if (emailHint.length > 0) {
          url.searchParams.set('loginHint', emailHint);
        }
        launchUrl = url.toString();
      } catch (error) {
        if (emailHint.length > 0) {
          const separator = baseStartUrl.includes('?') ? '&' : '?';
          launchUrl = `${baseStartUrl}${separator}loginHint=${encodeURIComponent(emailHint)}`;
        }
        console.warn('[test-login] failed to normalise startUrl', error);
      }

      const popup = window.open(
        launchUrl,
        'reap-test-login',
        'width=480,height=720,menubar=no,toolbar=no,location=no,status=no'
      );
      if (!popup) {
        setTestStatus('Popup blocked. Allow popups for this site and try again.');
        return;
      }
      setTestStatus('Complete the sign-in in the popup window.');

      const pollInterval = window.setInterval(async () => {
        try {
          if (popup.closed) {
            window.clearInterval(pollInterval);
            setTestStatus('Popup closed before completion.');
            return;
          }
          const popupUrl = popup.location.href;
          if (!popupUrl.includes('oidcTx') && !popupUrl.includes('samlTx') && !popupUrl.includes('loginTx')) {
            return;
          }
          const url = new URL(popupUrl);
          const oidcTx = url.searchParams.get('oidcTx');
          const samlTx = url.searchParams.get('samlTx');
          const grantType = oidcTx ? 'oidcTx' : samlTx ? 'samlTx' : null;
          if (!grantType) {
            return;
          }
          window.clearInterval(pollInterval);
          popup.close();
          setTestStatus('Exchanging token…');
          let request: TokenGrantRequest | null = null;
          if (grantType === 'oidcTx' && oidcTx) {
            request = {
              grantType: 'oidcTx',
              oidcTx,
              orgId
            };
          } else if (grantType === 'samlTx' && samlTx) {
            request = {
              grantType: 'samlTx',
              samlTx,
              orgId
            };
          }

          if (!request) {
            setTestStatus('Popup returned an unsupported auth handle.');
            setTestDetails(null);
            return;
          }

          const tokenResponse = await authApi.authTokenPost({ tokenRequest: toTokenRequest(request) });
          const tokens = getTokenResponseOrThrow(tokenResponse);
          await logoutTestSession(tokens.accessToken);
          setTestStatus('✅ Test login succeeded');
          setTestDetails(`Access token expires in ${tokens.expiresIn ?? '?'}s`);
        } catch (pollError) {
          if (pollError instanceof DOMException || (pollError as Error).message?.includes('cross-origin')) {
            return;
          }
          window.clearInterval(pollInterval);
          setTestStatus('Test login failed.');
          setTestDetails((pollError as Error).message);
        }
      }, 1000);
    } catch (caught: unknown) {
      if (caught instanceof ResponseError) {
        setTestStatus(`Test login failed (HTTP ${caught.response.status}).`);
      } else if (caught instanceof Error) {
        setTestStatus(`Test login failed: ${caught.message}`);
      } else {
        setTestStatus('Test login failed.');
      }
    } finally {
      setTestBusy(false);
    }
  };

  const overviewMetrics = useMemo(() => {
    if (!connection) {
      return [];
    }
    if (isOidcConnection(connection)) {
      return [
        { label: 'Issuer', value: connection.config.issuer },
        { label: 'Client ID', value: connection.config.clientId },
        { label: 'Token Auth', value: connection.config.tokenAuthMethod ?? 'client_secret_basic' },
        { label: 'Scopes', value: (connection.config.scopes ?? []).join(', ') || '—' },
        { label: 'PKCE', value: connection.config.enforcePkce ? 'Required' : 'Disabled' }
      ];
    }
    return [
      { label: 'SP Entity ID', value: connection.config.spEntityId ?? '—' },
      { label: 'ACS URL', value: connection.config.acsUrl ?? '—' },
      { label: 'NameID Format', value: connection.config.nameIdFormat ?? 'unspecified' },
      { label: 'Allow IdP-initiated', value: connection.config.allowIdpInitiated ? 'Yes' : 'No' }
    ];
  }, [connection]);

  const summaryEntries = useMemo<SummaryEntry[]>(() => {
    if (!connection) {
      return [];
    }
    const statusBadge = formatConnectionStatusBadge(connection.status);
    const sourceBadge = formatConnectionSourceBadge(deriveConnectionSource(connection));
    return [
      { label: 'Status', value: <span className={statusBadge.className}>{statusBadge.label}</span> },
      { label: 'Type', value: (connection.type ?? '—').toUpperCase() },
      { label: 'Source', value: <span className={sourceBadge.className}>{sourceBadge.label}</span> },
      { label: 'Updated', value: formatDateTime(connection.updatedAt ?? connection.createdAt ?? null) ?? '—' }
    ];
  }, [connection]);

  const metadataSummary = useMemo(() => {
    if (!connection) {
      return [];
    }
    if (isOidcConnection(connection)) {
      return [
        { label: 'Client Secret', value: connection.config.clientSecretId ? 'Configured' : 'Missing' },
        { label: 'Groups Claim', value: connection.config.groupsClaimPath ?? 'groups' },
        { label: 'Roles Claim', value: connection.config.rolesClaimPath ?? '—' },
        { label: 'Clock Skew', value: `${connection.config.clockSkewSeconds ?? 120}s` }
      ];
    }
    return [
      { label: 'Metadata URL', value: connection.config.idpMetadataUrl ?? '—' },
      { label: 'Entity ID', value: connection.config.idpEntityId ?? '—' },
      { label: 'SSO URL', value: connection.config.idpSsoUrl ?? '—' },
      { label: 'Clock Skew', value: `${connection.config.clockSkewSeconds ?? 120}s` }
    ];
  }, [connection]);

  const mappingRows = useMemo<MappingRow[]>(
    () =>
      mappingRules.map((rule, index) => ({
        id: rule.id ?? `${index}`,
        rule,
        preview: summarizeMappingRule(rule)
      })),
    [mappingRules]
  );

  const mappingPageCount = Math.max(1, Math.ceil(mappingRows.length / mappingPageSize));
  const pagedMappingRows = useMemo(() => {
    const start = (mappingPage - 1) * mappingPageSize;
    return mappingRows.slice(start, start + mappingPageSize);
  }, [mappingPage, mappingPageSize, mappingRows]);

  useEffect(() => {
    if (mappingPage > mappingPageCount) {
      setMappingPage(mappingPageCount);
    }
  }, [mappingPage, mappingPageCount]);

  const mappingColumns = useMemo<Array<ListWorkspaceColumn<MappingRow>>>(
    () => [
      {
        key: 'match',
        header: 'Match',
        render: (row) => row.preview.description
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (row) => (row.preview.roleSlugs.length > 0 ? row.preview.roleSlugs.join(', ') : '—')
      },
      {
        key: 'rule-status',
        header: 'Status',
        render: (row) => (row.preview.enabled ? 'Enabled' : 'Disabled')
      }
    ],
    []
  );

  const renderSummary = () => {
    if (!connection) {
      return null;
    }
    return (
      <div className="record-summary">
        <div className="record-summary__grid">
          {summaryEntries.map((entry) => (
            <div key={entry.label}>
              <span>{entry.label}</span>
              <strong>{entry.value}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const drawerTabs = useMemo(
    () => [
      { id: 'overview', label: 'Overview' },
      { id: 'test', label: 'Test Login' },
      { id: 'checklist', label: 'Setup Checklist' }
    ],
    []
  );

  const renderOverview = () => {
    if (!connection) {
      return null;
    }
    const statusBadge = formatConnectionStatusBadge(connection.status);
    const sourceBadge = formatConnectionSourceBadge(deriveConnectionSource(connection));
    return (
      <div className="form-grid">
        <section className="section-card">
          <div className="section-card__header">
            <div>
              <h3 className="section-card__title">{connection.name}</h3>
              <p className="section-card__subtitle">
                <span className={statusBadge.className}>{statusBadge.label}</span> •{' '}
                <span className={sourceBadge.className}>{sourceBadge.label}</span>
              </p>
            </div>
            <div className="mapping-rule-card__actions">
              <button type="button" className="ui-button" onClick={handleStatusToggle} disabled={actionBusy}>
                {connection.status === 'active' ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
          <div className="key-value">
            {overviewMetrics.map((metric) => (
              <Fragment key={metric.label}>
                <div className="key-value__key">{metric.label}</div>
                <div className="key-value__value">{metric.value}</div>
              </Fragment>
            ))}
          </div>
          <label className="form-field">
            <span className="form-field__label">Description</span>
            <input
              className="input"
              value={detailsDescription}
              onChange={(event) => {
                setDetailsDescription(event.target.value);
                setDetailsSuccess(null);
              }}
              placeholder="Internal description"
            />
          </label>
          {detailsError && (
            <p className="org-shell__banner" role="alert">
              {detailsError}
            </p>
          )}
          {detailsSuccess && <p className="form-field__hint">{detailsSuccess}</p>}
          {actionError && (
            <p className="org-shell__banner" role="alert">
              {actionError}
            </p>
          )}
        </section>

        <section className="section-card">
          <h3 className="section-card__title">Metadata</h3>
          <div className="key-value">
            {metadataSummary.map((item) => (
              <Fragment key={item.label}>
                <div className="key-value__key">{item.label}</div>
                <div className="key-value__value">{item.value}</div>
              </Fragment>
            ))}
          </div>
        </section>

        {isOidcConnection(connection) && (
          <section className="section-card">
            <h3 className="section-card__title">Client Secret</h3>
            <p className="form-field__hint">
              Stored securely via Secrets API. Provide a new value to rotate; the existing value is never shown.
            </p>
            <form onSubmit={handleRotateSecret} className="form-grid">
              <label className="form-field">
                <span className="form-field__label">New Client Secret</span>
                <input
                  className="input"
                  type="password"
                  value={rotateSecretValue}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setRotateSecretValue(event.target.value);
                    setRotateSecretError(null);
                    setRotateSecretSuccess(null);
                  }}
                  placeholder="Enter new client secret"
                  disabled={rotateSecretBusy}
                />
              </label>
              <div className="mapping-rule-card__actions">
                <button type="submit" className="ui-button ui-button--primary" disabled={rotateSecretBusy}>
                  {rotateSecretBusy ? 'Saving…' : 'Save New Secret'}
                </button>
              </div>
            </form>
            {rotateSecretError && (
              <p className="org-shell__banner" role="alert">
                {rotateSecretError}
              </p>
            )}
            {rotateSecretSuccess && <p className="form-field__hint">{rotateSecretSuccess}</p>}
          </section>
        )}

        {isOidcConnection(connection) && connection.config.redirectUris && connection.config.redirectUris.length > 0 && (
          <section className="section-card">
            <h3 className="section-card__title">Redirect URIs</h3>
            {connection.config.redirectUris.map((uri) => (
              <CopyField key={uri} value={uri} />
            ))}
          </section>
        )}

        {isSamlConnection(connection) && connection.config.idpCertificate && (
          <section className="section-card">
            <h3 className="section-card__title">IdP Certificate</h3>
            <textarea className="textarea" value={connection.config.idpCertificate} readOnly />
          </section>
        )}
      </div>
    );
  };


  const renderChecklist = () => (
    <div className="form-grid">
      <section className="section-card">
        <h3 className="section-card__title">Setup checklist</h3>
        <ol className="right-rail__list">
          <li>Confirm domain ownership and IdP admin access.</li>
          <li>Review default attribute mappings and directory groups.</li>
          <li>Run a Test Login before enabling the connection for users.</li>
        </ol>
      </section>
      <section className="section-card">
        <h3 className="section-card__title">Docs</h3>
        <ul className="right-rail__list">
          <li>
            <a href="https://help.okta.com" target="_blank" rel="noreferrer">
              Okta SSO configuration
            </a>
          </li>
          <li>
            <a href="https://learn.microsoft.com/azure/active-directory/" target="_blank" rel="noreferrer">
              Entra (Azure AD)
            </a>
          </li>
          <li>
            <a href="https://support.google.com/a/topic/7556592" target="_blank" rel="noreferrer">
              Google Workspace SAML
            </a>
          </li>
        </ul>
      </section>
    </div>
  );

  const renderMappingRules = () => (
    <div className="form-grid">
      <section className="section-card">
        <ListWorkspace
          headerLabel="Mapping rules"
          rows={pagedMappingRows}
          rowId={(row) => row.id}
          columns={mappingColumns}
          page={mappingPage}
          pageSize={mappingPageSize}
          totalCount={mappingRows.length}
          onPageChange={(next) => setMappingPage(Math.max(1, next))}
          onPageSizeChange={(size) => {
            setMappingPageSize(size);
            setMappingPage(1);
          }}
          emptyState={<p>No mapping rules defined yet.</p>}
          loading={mappingLoading}
          onAdd={handleMappingAdd}
          onDeleteSelected={(rows) => handleBulkDeleteRules(rows as MappingRow[])}
          onRowClick={(row) => handleEditRule(row.rule)}
          rowActions={(row) => (
            <button
              type="button"
              className="list-workspace__icon-button"
              aria-label={row.rule.enabled ? 'Disable rule' : 'Enable rule'}
              title={row.rule.enabled ? 'Disable rule' : 'Enable rule'}
              onClick={() => handleMappingToggle(row.rule)}
            >
              {row.rule.enabled ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
            </button>
          )}
        />
        {mappingError && (
          <p className="org-shell__banner" role="alert">
            {mappingError}
          </p>
        )}
      </section>
    </div>
  );
  const renderTestLogin = () => (
    <div className="form-grid">
      <div className="section-card">
        <h3 className="section-card__title">Run Test Login</h3>
        <label className="form-field">
          <span className="form-field__label">Email (optional login hint)</span>
          <input
            className="input"
            value={testEmail}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setTestEmail(event.target.value)}
            placeholder="someone@example.com"
          />
        </label>
        <button type="button" className="ui-button ui-button--primary" onClick={handleTestLogin} disabled={testBusy}>
          {testBusy ? 'Running…' : 'Start Test'}
        </button>
        {testStatus && (
          <p className="form-field__hint">
            {testStatus}
            {testDetails && <><br />{testDetails}</>}
          </p>
        )}
      </div>
    </div>
  );

  const renderMappingRuleDrawer = () => (
    <Drawer
      open={mappingDrawerOpen}
      onClose={handleMappingDrawerClose}
      title={isEditing ? 'Edit mapping rule' : 'New mapping rule'}
      actions={
        <button
          type="submit"
          form="mapping-rule-form"
          className="drawer-icon-button"
          aria-label="Save rule"
          title="Save rule"
          disabled={mappingSubmitting}
        >
          <SaveIcon size={18} />
        </button>
      }
    >
      <form id="mapping-rule-form" className="form-grid form-grid--two" onSubmit={handleMappingSubmit}>
        <label className="form-field">
          <span className="form-field__label">Source</span>
          <select className="select" name="source" value={mappingForm.source} onChange={handleMappingFormChange}>
            <option value="group">Group</option>
            <option value="claim">Claim</option>
            <option value="attribute">Attribute</option>
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Operator</span>
          <select className="select" name="operator" value={mappingForm.operator} onChange={handleMappingFormChange}>
            <option value="equals">equals</option>
            <option value="contains">contains</option>
            <option value="matches">matches</option>
          </select>
        </label>
        {(mappingForm.source === 'claim' || mappingForm.source === 'attribute') && (
          <label className="form-field">
            <span className="form-field__label">{mappingForm.source === 'claim' ? 'Path' : 'Attribute'}</span>
            <input className="input" name="path" value={mappingForm.path} onChange={handleMappingFormChange} />
          </label>
        )}
        <label className="form-field">
          <span className="form-field__label">Match value</span>
          <input className="input" name="value" value={mappingForm.value} onChange={handleMappingFormChange} />
        </label>
        <label className="form-field">
          <span className="form-field__label">Result role slugs</span>
          {rolesError && (
            <span className="form-field__hint" role="alert">
              {rolesError}
            </span>
          )}
          <div className="chip-list" role="list">
            {roleSlugChips.map((chip) => {
              const label = formatSelectedRoleLabel(chip.slug);
              return (
                <button
                  key={chip.id}
                  type="button"
                  className="chip-button"
                  onClick={() => handleRoleSlugRemove(chip.id)}
                  aria-label={`Remove ${label}`}
                  role="listitem"
                >
                  {label}
                  <span aria-hidden="true"> ×</span>
                </button>
              );
            })}
          </div>
          <select
            className="select"
            value={roleSlugSelection}
            onChange={handleRoleSlugSelect}
            disabled={rolesLoading || availableRoleOptions.length === 0}
          >
            <option value="" disabled>
              {rolesLoading
                ? 'Loading role slugs…'
                : availableRoleOptions.length === 0
                ? 'No additional slugs available'
                : 'Select a role slug'}
            </option>
            {availableRoleOptions.map((role) => (
              <option key={role.id ?? role.slug} value={role.slug}>
                {formatRoleOptionLabel(role)}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field checkbox-row">
          <input type="checkbox" className="checkbox" name="enabled" checked={mappingForm.enabled} onChange={handleMappingFormChange} />
          <span>Enabled</span>
        </label>
        {mappingError && (
          <p className="org-shell__banner" role="alert">
            {mappingError}
          </p>
        )}
      </form>
    </Drawer>
  );

  const renderDrawerActions = () => {
    if (!connection) {
      return null;
    }
    return (
      <div className="drawer-icon-actions">
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Delete connection"
          title="Delete connection"
          onClick={() => setDeleteConfirmationOpen(true)}
          disabled={actionBusy}
        >
          <TrashIcon size={18} />
        </button>
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Save connection"
          title="Save connection"
          onClick={handleSaveDetails}
          disabled={detailsSaving}
        >
          <SaveIcon size={18} />
        </button>
      </div>
    );
  };

  if (!open) {
    return null;
  }

  const drawerContent = (
    <Drawer
      open={open}
      onClose={() => {
        if (!actionBusy && !deleteConfirmationOpen && !mappingDeleteBusy && !pendingMappingDeletes) {
          onClose();
        }
      }}
      title="Connection details"
      subtitle={connection ? connection.name ?? connection.id ?? '' : 'Loading…'}
      width="wide"
      actions={renderDrawerActions()}
    >
      {loading && <p>Loading connection…</p>}
      {error && (
        <p className="org-shell__banner" role="alert">
          {error}
        </p>
      )}
      {!loading && connection && (
        <>
          <Tabs items={drawerTabs} activeId={activeTab} onChange={(id) => setActiveTab(id as DrawerTab)} />
          <div className="ui-tabs__panel">
            {activeTab === 'overview' && (
              <div className="drawer-section-stack">
                {renderSummary()}
                {renderOverview()}
                {renderMappingRules()}
              </div>
            )}
            {activeTab === 'test' && renderTestLogin()}
            {activeTab === 'checklist' && renderChecklist()}
          </div>
        </>
      )}
    </Drawer>
  );

  return (
    <>
      {drawerContent}
      {renderMappingRuleDrawer()}
      <DeleteConfirmationModal
        open={deleteConfirmationOpen}
        title="Delete IdP connection?"
        description={`Delete ${connection?.name ?? 'this connection'}? This cannot be undone.`}
        deleting={actionBusy}
        onCancel={() => setDeleteConfirmationOpen(false)}
        onConfirm={() => void handleDelete()}
      />
      <DeleteConfirmationModal
        open={pendingMappingDeletes != null}
        title={pendingMappingDeletes?.length === 1 ? 'Delete mapping rule?' : `Delete ${pendingMappingDeletes?.length ?? 0} mapping rules?`}
        description="This permanently removes the selected role mapping rules."
        deleting={mappingDeleteBusy}
        onCancel={() => setPendingMappingDeletes(null)}
        onConfirm={() => void confirmMappingRuleDeletes()}
      />
    </>
  );
};

export default ConnectionDrawer;
